import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';
import { BookOpen, Brain, TrendingUp, Target, Check, AlertTriangle, Lightbulb } from 'lucide-react';
import { useAILearningFeedback } from '@/hooks/useAILearningFeedback';

interface CorrectionItem {
  id: string;
  field_name: string;
  original_value: string;
  corrected_value: string;
  confidence_before: number;
  confidence_after?: number;
}

interface CorrectionWorkflowProps {
  billId: string;
  queueItemId?: string;
  originalData: Record<string, any>;
  correctedData: Record<string, any>;
  onComplete: () => void;
}

export const CorrectionWorkflow: React.FC<CorrectionWorkflowProps> = ({
  billId,
  queueItemId,
  originalData,
  correctedData,
  onComplete
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  
  const { createFeedback, applyLearning, getFeedbackStats, isCreating } = useAILearningFeedback();

  // Identify corrections made
  const corrections: CorrectionItem[] = Object.keys(correctedData).reduce((acc, key) => {
    if (originalData[key] !== correctedData[key]) {
      acc.push({
        id: `${key}-correction`,
        field_name: key,
        original_value: originalData[key]?.toString() || '',
        corrected_value: correctedData[key]?.toString() || '',
        confidence_before: Math.random() * 100, // In real implementation, get from AI
        confidence_after: Math.min((Math.random() * 100) + 20, 100)
      });
    }
    return acc;
  }, [] as CorrectionItem[]);

  const handleProcessCorrections = async () => {
    setIsProcessing(true);
    
    try {
      // Step 1: Create learning feedback
      setCurrentStep(1);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate processing
      
      const feedbackData = {
        bill_id: billId,
        queue_item_id: queueItemId,
        feedback_type: 'correction' as const,
        original_extraction: originalData,
        corrected_extraction: correctedData,
        confidence_before: 85, // Would come from AI extraction
        confidence_after: 95,
        field_corrections: corrections.reduce((acc, correction) => {
          acc[correction.field_name] = {
            original: correction.original_value,
            corrected: correction.corrected_value,
            reason: 'manual_review'
          };
          return acc;
        }, {} as Record<string, any>)
      };

      await createFeedback(feedbackData);

      // Step 2: Apply learning patterns
      setCurrentStep(2);
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Step 3: Update model confidence
      setCurrentStep(3);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "AI Learning Complete",
        description: `Successfully processed ${corrections.length} corrections for future improvements.`,
      });

      setCurrentStep(4);
      setTimeout(onComplete, 1000);

    } catch (error) {
      toast({
        title: "Error processing corrections",
        description: "Failed to apply learning from corrections.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getStepProgress = () => {
    return (currentStep / 4) * 100;
  };

  const getStepStatus = (step: number) => {
    if (step < currentStep) return 'completed';
    if (step === currentStep) return 'active';
    return 'pending';
  };

  const stats = getFeedbackStats();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            AI Learning Workflow
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Progress Indicator */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Processing Progress</span>
                <span className="text-sm text-muted-foreground">{Math.round(getStepProgress())}%</span>
              </div>
              <Progress value={getStepProgress()} className="h-2" />
            </div>

            {/* Steps */}
            <div className="space-y-4">
              {[
                { step: 1, title: 'Create Learning Feedback', icon: BookOpen },
                { step: 2, title: 'Apply Learning Patterns', icon: Target },
                { step: 3, title: 'Update Model Confidence', icon: TrendingUp },
                { step: 4, title: 'Complete', icon: Check }
              ].map(({ step, title, icon: Icon }) => {
                const status = getStepStatus(step);
                return (
                  <div key={step} className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      status === 'completed' ? 'bg-green-100 text-green-600' :
                      status === 'active' ? 'bg-primary text-primary-foreground animate-pulse' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <span className={`${
                      status === 'completed' ? 'text-green-600 font-medium' :
                      status === 'active' ? 'text-primary font-medium' :
                      'text-muted-foreground'
                    }`}>
                      {title}
                    </span>
                    {status === 'completed' && (
                      <Check className="h-4 w-4 text-green-600 ml-auto" />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Action Button */}
            {currentStep < 4 && (
              <Button 
                onClick={handleProcessCorrections}
                disabled={isProcessing || isCreating}
                className="w-full"
              >
                {isProcessing ? 'Processing...' : 'Start AI Learning Process'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="corrections" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="corrections">Corrections Made</TabsTrigger>
          <TabsTrigger value="patterns">Learning Patterns</TabsTrigger>
          <TabsTrigger value="impact">Expected Impact</TabsTrigger>
        </TabsList>

        <TabsContent value="corrections" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Field Corrections</CardTitle>
            </CardHeader>
            <CardContent>
              {corrections.length > 0 ? (
                <div className="space-y-4">
                  {corrections.map((correction) => (
                    <div key={correction.id} className="p-4 border rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">{correction.field_name}</Badge>
                        <div className="text-sm text-muted-foreground">
                          Confidence: {correction.confidence_before.toFixed(1)}% → {correction.confidence_after?.toFixed(1)}%
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-destructive">Original:</span>
                          <div className="mt-1 p-2 bg-destructive/10 rounded">{correction.original_value}</div>
                        </div>
                        <div>
                          <span className="font-medium text-green-600">Corrected:</span>
                          <div className="mt-1 p-2 bg-green-50 rounded">{correction.corrected_value}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                  <p>No corrections were made to the extracted data.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="patterns" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Learning Patterns Identified</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-blue-900">Pattern Recognition</span>
                  </div>
                  <p className="text-sm text-blue-800">
                    The AI will learn from these corrections to improve future extractions for similar documents.
                  </p>
                </div>
                
                {corrections.map((correction, index) => (
                  <div key={index} className="p-3 border rounded">
                    <span className="font-medium">{correction.field_name} pattern:</span>
                    <p className="text-sm text-muted-foreground mt-1">
                      When encountering "{correction.original_value}", consider "{correction.corrected_value}" as the correct interpretation.
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="impact" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Expected Impact</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-green-900">+{corrections.length * 5}%</div>
                  <div className="text-sm text-green-700">Accuracy Improvement</div>
                </div>
                
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Target className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-blue-900">{corrections.length}</div>
                  <div className="text-sm text-blue-700">Fields Enhanced</div>
                </div>
                
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <Brain className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-purple-900">{stats.totalFeedback + 1}</div>
                  <div className="text-sm text-purple-700">Total Learning Sessions</div>
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="font-medium text-amber-900">Future Benefits</span>
                </div>
                <ul className="text-sm text-amber-800 space-y-1">
                  <li>• Improved recognition for similar document types</li>
                  <li>• Reduced manual review requirements</li>
                  <li>• Enhanced confidence scores for future extractions</li>
                  <li>• Better handling of edge cases and variations</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};